import React, { useState, useEffect } from "react";
import { TradingSymbol } from "@/utils/mockData";
import { Star, ArrowDown, ArrowUp, Clock, Tag, Calendar, Trash2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card"; 
import { useIsMobile } from "@/hooks/use-mobile";
import { usePriceContext } from "@/context/PriceContext";
import { useSwipeable } from "react-swipeable";
import { useWatchlist } from "@/context/WatchlistContext";
import TradingModal from "@/components/TradingModal";
import TradingChartModal from "@/components/TradingChartModal";
import { MobileTradingDrawer } from "@/components/MobileTradingDrawer";
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from "@/components/ui/collapsible";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { apiClient } from "@/utils/apiClient";

interface TradingCardProps {
  symbol: TradingSymbol;
  isFavorite: boolean;
  onToggleFavorite: (symbolName: string) => void;
  isCompact?: boolean;
  hideStarInWatchlist?: boolean;
  onAskAIClick?: () => void;
}

const TradingCard: React.FC<TradingCardProps> = ({ 
  symbol, 
  isFavorite, 
  onToggleFavorite,
  isCompact = false,
  hideStarInWatchlist = false,
  onAskAIClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSwipeOpen, setIsSwipeOpen] = useState(false);
  const isMobile = useIsMobile();
  const { symbols } = usePriceContext();
  const { removeFromWatchlist, activeView } = useWatchlist();
  const { user } = useAuth();
  
  // Trading modal state
  const [isTradingModalOpen, setIsTradingModalOpen] = useState(false);
  const [tradeType, setTradeType] = useState<"buy" | "sell">("buy");
  
  // Chart modal state
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  
  // Mobile drawer state
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  
  // Check if trading is enabled for the user
  const isTradingEnabled = user?.account?.is_trading === true;
  
  // Enhanced format price function with dynamic decimal places based on digit parameter
  const formatPrice = (price: number, digitPrecision?: number): string => {
    if (price === null || price === undefined) return "--";
    
    // Use provided digit precision, otherwise fall back to symbol.digit, otherwise show raw value
    const precision = digitPrecision !== undefined ? digitPrecision : symbol.digit;
    
    if (precision !== undefined && precision !== null) {
      return price.toFixed(precision);
    }
    
    // If digit is missing or undefined, display raw value
    return price.toString();
  };
  
  // Consistent LTP calculation - use feed.ltp if available, fallback to average of bid/ask
  const getLastTradePrice = (): number => {
    // Priority: symbol._feedData.ltp > WebSocket average > fallback to 0
    if (symbol._feedData?.ltp && symbol._feedData.ltp > 0) {
      return symbol._feedData.ltp;
    }
    
    // Fallback to average of buy/sell prices if both are valid
    if (symbol.buyPrice > 0 && symbol.sellPrice > 0) {
      return (symbol.buyPrice + symbol.sellPrice) / 2;
    }
    
    return 0;
  };
  
  // Get consistent price values with feed fallback for mobile and desktop sync
  const getConsistentPrice = (webSocketValue: any, feedValue: any, defaultValue = 0): number => {
    const isValid = (value: any) =>
      value !== null && value !== undefined && value !== "" && value !== 0;

    if (isValid(webSocketValue)) return webSocketValue;
    if (isValid(feedValue)) return feedValue;
    return defaultValue;
  };
  
  // Consistent bid price across mobile and desktop
  const consistentBidPrice = getConsistentPrice(symbol.buyPrice, symbol._feedData?.b || 0);  
  
  // Consistent ask price across mobile and desktop  
  const consistentAskPrice = getConsistentPrice(symbol.sellPrice, symbol._feedData?.a || 0);
  
  // Consistent high price across mobile and desktop
  const consistentHighPrice = getConsistentPrice(symbol.high, symbol._feedData?.h || 0);
  
  // Consistent low price across mobile and desktop
  const consistentLowPrice = getConsistentPrice(symbol.low, symbol._feedData?.l || 0);
  
  // Consistent LTP across mobile and desktop
  const consistentLTP = getLastTradePrice();
  
  // Updated format LTT with milliseconds precision - consistent across mobile and desktop
  const formatLTT = (timestamp?: number): string => {
    if (!timestamp) return "--:--:--.---";
    
    const date = new Date(timestamp);
    
    // Format: HH:MM:SS.mmm (24-hour format with milliseconds)
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
    
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
  };
  
  // Get formatted LTT or use placeholder if not available
  const lttDisplay = formatLTT(symbol.lastTradeTime);
  
  // Format volume with appropriate scaling and 2 decimal places - enhanced to never show 0
  const formatVolume = (volume?: number): string => {
    if (volume === undefined || volume === null || volume <= 0) return "--";
    
    if (volume >= 1000000) {
      return `${(volume / 1000000).toFixed(2)}M`;
    } else if (volume >= 1000) {
      return `${(volume / 1000).toFixed(2)}K`;
    } else {
      return volume.toFixed(2);
    }
  };
  
  // Function to determine the CSS class for volume change - enhanced with preservation logic
  const getVolumeChangeClass = (current?: number, previous?: number, preserved?: boolean): string => {
    // If value was preserved (incoming was 0), show grey
    if (preserved) return "text-muted-foreground";
    
    if (current === undefined || previous === undefined) return "text-muted-foreground";
    if (current > previous) return "text-positive";
    if (current < previous) return "text-negative";
    return "text-muted-foreground";
  };
  
  // Mock expiry date - in a real app this would come from API
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 30);
  const expiryFormatted = expiryDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

  // Enhanced format spread to never show 0 - consistent across mobile and desktop
  const formatSpread = (spread: number): string => {
    if (spread <= 0) return "--";
    
    // Ensure 2 digits before decimal and 2 after
    return spread.toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // Calculate consistent spread using consistent prices
  const consistentSpread = (consistentBidPrice > 0 && consistentAskPrice > 0) 
    ? Math.abs(consistentAskPrice - consistentBidPrice) 
    : 0;
  
  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    console.log('Favorite button clicked for symbol:', symbol.name);
    console.log('Current favorite status:', isFavorite);
    console.log('Sending API request to /api/favourites with payload:', {
      symbol: symbol.name,
      favourite: !isFavorite
    });
    
    try {
      // Call API to update favorite status
      const response = await apiClient.post('favourites', {
        symbol: symbol.name,
        favourite: !isFavorite
      });
      
      console.log('API response:', response);
      
      // Update UI
      onToggleFavorite(symbol.name);
      
      toast({
        title: isFavorite ? "Removed from Favorites" : "Added to Favorites",
        description: `${symbol.name} ${isFavorite ? 'removed from' : 'added to'} favorites`,
      });
    } catch (error) {
      console.error('Failed to update favorite status:', error);
      toast({
        title: "Error",
        description: "Failed to update favorite status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  
  // Open chart modal when symbol is clicked (desktop) or drawer (mobile)
  const handleCardClick = () => {
    if (isMobile) {
      setIsMobileDrawerOpen(true);
    } else {
      setIsChartModalOpen(true);
    }
  };

  // Open chart modal with candlestick format for mobile
  const openChartModalWithCandlestick = () => {
    setIsChartModalOpen(true);
  };
  
  // COMMENTED OUT: Trading modal opening logic for Trade Now button
  const handleTradeNowClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    
    // Check if trading is enabled before opening modal
    // if (isTradingEnabled) {
    //   setTradeType("buy");
    //   setIsTradingModalOpen(true);
    // } else {
    //   // Notify user that trading is not enabled
    //   toast({
    //     title: "Trading Disabled",
    //     description: "Trading is not enabled for your account. Please contact support.",
    //     variant: "destructive",
    //   });
    // }
  };
  
  // Handle symbol name click to open chart modal (for desktop) or drawer (for mobile)
  const handleSymbolNameClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent overall card click
    if (isMobile) {
      setIsMobileDrawerOpen(true);
    } else {
      setIsChartModalOpen(true);
    }
  };
  
  // Handle symbol removal from watchlist
  const handleRemoveFromWatchlist = () => {
    removeFromWatchlist(symbol.name);
    setIsSwipeOpen(false);
    toast({
      title: "Symbol Removed",
      description: `${symbol.name} removed from watchlist`,
    });
  };

  // Enhanced price color functions with grey for unchanged prices and preserved prices
  const getBidPriceColor = () => {
    // If price was preserved (incoming was 0), show grey
    if (symbol.pricePreserved?.buyPrice) return "text-muted-foreground";
   
    if (!symbol.previousBuyPrice || consistentBidPrice === symbol.previousBuyPrice) return "text-muted-foreground";
    if (consistentBidPrice > symbol.previousBuyPrice) return "text-positive";
    if (consistentBidPrice < symbol.previousBuyPrice) return "text-negative";
    return "text-muted-foreground";
  };

  const getAskPriceColor = () => {
    // If price was preserved (incoming was 0), show grey
    if (symbol.pricePreserved?.sellPrice) return "text-muted-foreground";
    
    if (!symbol.previousSellPrice || consistentAskPrice === symbol.previousSellPrice) return "text-muted-foreground";
    if (consistentAskPrice > symbol.previousSellPrice) return "text-positive";
    if (consistentAskPrice < symbol.previousSellPrice) return "text-negative";
    return "text-muted-foreground";
  };

  // Add color functions for High and Low prices with preservation logic
  const getHighPriceColor = () => {
    // If price was preserved (incoming was 0), show grey
    if (symbol.pricePreserved?.high) return "text-muted-foreground";
    
    if (!symbol.previousHigh || consistentHighPrice === symbol.previousHigh) return "text-muted-foreground";
    if (consistentHighPrice > symbol.previousHigh) return "text-positive";
    if (consistentHighPrice < symbol.previousHigh) return "text-negative";
    return "text-muted-foreground";
  };

  const getLowPriceColor = () => {
    // If price was preserved (incoming was 0), show grey
    if (symbol.pricePreserved?.low) return "text-muted-foreground";
    
    if (!symbol.previousLow || consistentLowPrice === symbol.previousLow) return "text-muted-foreground";
    if (consistentLowPrice > symbol.previousLow) return "text-positive";
    if (consistentLowPrice < symbol.previousLow) return "text-negative";
    return "text-muted-foreground";
  };

  // Enhanced LTP color based on overall change with preservation logic
  const getLTPColor = () => {
    if (consistentLTP <= 0) return "text-muted-foreground";
    
    const previousLTP = (symbol.previousBuyPrice + symbol.previousSellPrice) / 2;
    
    // If either bid or ask was preserved, show grey for LTP
    if (symbol.pricePreserved?.buyPrice || symbol.pricePreserved?.sellPrice) return "text-muted-foreground";
    
    if (!previousLTP || consistentLTP === previousLTP) return "text-muted-foreground";
    if (consistentLTP > previousLTP) return "text-positive";
    if (consistentLTP < previousLTP) return "text-negative";
    return "text-muted-foreground";
  };
  
  // Updated related symbol price color logic to include neutral state
  const getRelatedBidColor = (currentBid: number | string, previousBid: number | null): string => {
    if (typeof currentBid === 'string' || !previousBid || currentBid === previousBid) return "text-muted-foreground";
    if (currentBid > previousBid) return "text-positive";
    if (currentBid < previousBid) return "text-negative";
    return "text-muted-foreground";
  };
  
  const getRelatedAskColor = (currentAsk: number | string, previousAsk: number | null): string => {
    if (typeof currentAsk === 'string' || !previousAsk || currentAsk === previousAsk) return "text-muted-foreground";
    if (currentAsk > previousAsk) return "text-positive";
    if (currentAsk < previousAsk) return "text-negative";
    return "text-muted-foreground";
  };
  
  const isBadlaInstrument = symbol.intrumentType === "BADLA";
  
  // Configure swipe handlers - UPDATED to work correctly
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (activeView === 'favorites') {
        setIsSwipeOpen(true);
      }
    },
    onSwipedRight: () => {
      if (activeView === 'favorites' && isSwipeOpen) {
        setIsSwipeOpen(false);
      }
    },
    trackMouse: true, // Enable for testing on desktop
    delta: 50,
    preventScrollOnSwipe: true,
  });
  
  // Close swipe when clicking elsewhere - UPDATED with better event handling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click was outside the card
      const swipeElement = document.querySelector('.swipe-container');
      if (isSwipeOpen && swipeElement && !swipeElement.contains(event.target as Node)) {
        setIsSwipeOpen(false);
      }
    };
    
    if (isSwipeOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isSwipeOpen]);

  // Return a compact card for table rows with fixed alignment
  if (isCompact) {
    return (
      <>
        <div 
          className="flex items-center py-1.5 hover:bg-card/30 transition-colors border-b border-border/20 symbol-card cursor-pointer"
          onClick={handleCardClick}
        >
          <div className="flex items-center flex-1 min-w-0 overflow-hidden pr-1">
            {!hideStarInWatchlist && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-5 w-5 p-0 mr-1 flex-shrink-0" 
                onClick={toggleFavorite}
                aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
              >
                <Star 
                  size={14} 
                  className={isFavorite ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"} 
                />
              </Button>
            )}
            <span 
              className="font-medium truncate text-theme-light cursor-pointer hover:text-primary transition-colors"
              onClick={handleSymbolNameClick}
            >
              {symbol.name}
            </span>
            <div className={`ml-1 px-1 py-0.5 rounded text-xs flex-shrink-0 ${symbol.change >= 0 ? "bg-positive/20 text-positive" : "bg-negative/20 text-negative"}`}>
              <div className="flex items-center gap-0.5">
                {symbol.change >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                <span className="whitespace-nowrap">{symbol.change >= 0 ? "+" : ""}{symbol.change}%</span>
              </div>
            </div>
          </div>
          
          {/* Fixed alignment for Bid and Ask columns */}
          <div className="flex items-center flex-shrink-0">
            {/* Bid Price - Fixed width for proper column alignment */}
            <div className="w-16 sm:w-20 text-right font-medium">
              <span className={`${getBidPriceColor()} transition-colors duration-300`}>
                {formatPrice(consistentBidPrice)}
              </span>
            </div>
            
            {/* Ask Price - Fixed width for proper column alignment */}
            <div className="w-16 sm:w-20 text-right ml-1 sm:ml-4">
              <span className={`${getAskPriceColor()} transition-colors duration-300`}>
                {formatPrice(consistentAskPrice)}
              </span>
            </div>
            
            {!isMobile && (
              <Button 
                size="sm"
                variant="outline"
                className="ml-2 text-xs h-7 bg-primary/10 hover:bg-primary/20 border-primary/20"
                onClick={handleTradeNowClick}
              >
                Trade Now
              </Button>
            )}
          </div>
        </div>
        
        {/* COMMENTED OUT: Trading Modal */}
        {/* {isTradingModalOpen && (
          <TradingModal
            open={isTradingModalOpen}
            onOpenChange={setIsTradingModalOpen}
            symbol={symbol}
            tradeType={tradeType}
          />
        )} */}
      </>
    );
  }

  // Calculate absolute price difference (for display) using consistent LTP
  const absoluteDifference = (symbol.change / 100) * consistentLTP;

  // Apply swipe handlers to all cards in watchlist/favorites view
  const swipeableProps = activeView === 'favorites' ? swipeHandlers : {};
  
  // Process related symbols for BADLA instruments using proper symbol mapping
  let exchangeData: Record<string, any> = {};
  
  if (isBadlaInstrument && symbol.relatedSymbols) {
    // Parse related symbols string - handles both object-like strings and comma-separated lists
    const relatedSymbolsString = typeof symbol.relatedSymbols === 'string' ? symbol.relatedSymbols : '';
    const relatedList = relatedSymbolsString.replace(/[{}]/g, "").trim();
    const relatedArray = relatedList ? relatedList.split(",").map(s => s.trim()) : [];
    
    // Map each related symbol to its actual data from the symbols array
    if (relatedArray.length > 0) {
      relatedArray.forEach(relatedSymbolName => {
        // Find the actual symbol data using the name field for display and symbol field for data
        const relatedSymbolData = symbols.find(s => s.name === relatedSymbolName || s.symbol === relatedSymbolName);
        
        if (relatedSymbolData) {
          // Use consistent price logic for related symbols too
          const relatedBidPrice = getConsistentPrice(relatedSymbolData.buyPrice, relatedSymbolData._feedData?.b || 0);
          const relatedAskPrice = getConsistentPrice(relatedSymbolData.sellPrice, relatedSymbolData._feedData?.a || 0);
          
          exchangeData[relatedSymbolData.name] = {
            bid: relatedBidPrice,
            ask: relatedAskPrice,
            previousBid: relatedSymbolData.previousBuyPrice,
            previousAsk: relatedSymbolData.previousSellPrice,
            digit: relatedSymbolData.digit, // Store the digit precision for each related symbol
          };
        }
      });
    }
  }
  
  // Return updated trading card layout with consistent pricing across mobile and desktop
  return (
    <>
      <div className="relative overflow-hidden swipe-container" {...swipeableProps}>
        {/* Delete Action - visible when swiped */}
        {activeView === 'favorites' && (
          <div 
            className={`absolute right-0 top-0 bottom-0 flex items-center justify-center transition-all duration-300 ease-in-out bg-red-500 text-white z-10 w-[70px] ${isSwipeOpen ? 'translate-x-0' : 'translate-x-full'}`}
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveFromWatchlist();
            }}
          >
            <Trash2 size={24} />
          </div>
        )}
      
        <Card 
          className={`border-border bg-card/50 hover:bg-card/80 transition-colors p-0 overflow-hidden shadow-sm rounded-md ${isSwipeOpen ? '-translate-x-[70px]' : 'translate-x-0'} transition-transform duration-300 cursor-pointer`}
          onClick={handleCardClick}
        >
          
          <div>
            <div className={`${isMobile ? 'p-2 space-y-1.5' : 'p-3 space-y-2'}`}>
              {/* Row 1: Symbol Name, Favorite Icon, Bid/Ask Prices - Removed labels and increased font sizes */}
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1 min-w-0 overflow-hidden">
                  {/* Favorite Button - Hidden in watchlist view on mobile */}
                  {!hideStarInWatchlist && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className={`${isMobile ? 'h-5 w-5 mr-0.5' : 'h-6 w-6 mr-1.5'} p-0 flex-shrink-0`}
                      onClick={(e) => {
                        console.log('Desktop favorite button clicked!', {isMobile, symbol: symbol.name});
                        toggleFavorite(e);
                      }}
                      aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                    >
                      <Star 
                        size={isMobile ? 14 : 16} 
                        className={isFavorite ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"} 
                      />
                    </Button>
                  )}
                  
                  {/* COMMENTED OUT: Symbol Name click handler */}
                  <span 
                    className="font-bold text-sm truncate text-theme-light"
                    // onClick={handleSymbolNameClick}
                  >
                    {symbol.name}
                  </span>
                  
                  
                </div>
                
                {/* Enhanced Bid & Ask Prices without labels and increased font sizes for mobile */}
                <div className={`flex ${isMobile ? 'gap-3' : 'gap-4'} flex-shrink-0 items-center`}>
                  {/* Bid Column - removed label, increased font size for mobile */}
                  <div className={`font-medium ${isMobile ? 'w-[70px]' : 'w-20'} text-right`}>
                    <div className={`${getBidPriceColor()} transition-colors duration-300 ${isMobile ? 'text-base' : ''} ${!isMobile ? 'border-r border-border/30 pr-2' : ''}`}>
                      {formatPrice(consistentBidPrice)}
                    </div>
                  </div>
                  
                  {/* Ask Column - removed label, increased font size for mobile */}
                  <div className={`font-medium ${isMobile ? 'w-[70px]' : 'w-20'} text-right`}>
                    <div className={`${getAskPriceColor()} transition-colors duration-300 ${isMobile ? 'text-base' : ''}`}>
                      {formatPrice(consistentAskPrice)}
                    </div>
                  </div>
                  
                  {!isMobile && (
                    <Button 
                      size="sm"
                      variant="outline"
                      className="ml-2 text-xs bg-primary/10 hover:bg-primary/20 border-primary/20"
                      onClick={handleTradeNowClick}
                    >
                      Trade Now
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Row 2: LTT, Spread, Low & High with consistent values across mobile and desktop */}
              <div className="flex justify-between items-center text-xs flex-wrap gap-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Last Trade Time - Consistent format with milliseconds */}
                  <div className="text-theme-light whitespace-nowrap flex items-center gap-0.5">
                    <Clock size={12} className="text-theme-light/70" />
                    <span className="font-mono">{lttDisplay}</span>
                  </div>
                  
                  {/* Spread - Using consistent spread calculation */}
                  <div className="flex items-center whitespace-nowrap">
                    <span className="text-theme-light">S: </span>
                    <span className="text-theme-light">{formatSpread(consistentSpread)}</span>
                  </div>
                  
                  {/* Bid and Ask Volumes - Enhanced with preservation tracking */}
                  {!isMobile && (
                    <>
                      <div className="flex items-center whitespace-nowrap">
                        <span className="text-theme-light">BVol: </span>
                        <span className={`${getVolumeChangeClass(symbol.bidVolume, symbol.previousBidVolume, symbol.pricePreserved?.bidVolume)} transition-colors duration-300`}>
                          {formatVolume(symbol.bidVolume)}
                        </span>
                      </div>
                      <div className="flex items-center whitespace-nowrap">
                        <span className="text-theme-light">AVol: </span>
                        <span className={`${getVolumeChangeClass(symbol.askVolume, symbol.previousAskVolume, symbol.pricePreserved?.askVolume)} transition-colors duration-300`}>
                          {formatVolume(symbol.askVolume)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Low & High with consistent values and color logic */}
                <div className="flex gap-4">
                  <div className="whitespace-nowrap">
                    <span className="text-theme-light">L: </span>
                    <span className={`${getLowPriceColor()} transition-colors duration-300`}>{formatPrice(consistentLowPrice)}</span>
                  </div>
                  <div className="whitespace-nowrap">
                    <span className="text-theme-light">H: </span>
                    <span className={`${getHighPriceColor()} transition-colors duration-300`}>{formatPrice(consistentHighPrice)}</span>
                  </div>
                </div>
              </div>
              
              {/* Row 3: Difference (absolute), Difference %, LTP with consistent LTP across views and increased font size for mobile */}
              <div className="flex items-center justify-between flex-wrap gap-y-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* Absolute Difference - Using consistent LTP */}
                  <div className={`text-xs ${symbol.change >= 0 ? "text-positive" : "text-negative"} whitespace-nowrap`}>
                    {consistentLTP > 0 ? `${symbol.change >= 0 ? "+" : ""}${((symbol.change / 100) * consistentLTP).toFixed(2)}` : "--"}
                  </div>
                  
                  {/* Percentage Difference */}
                  <div className={`flex items-center px-1 py-0.5 rounded text-xs ${symbol.change >= 0 ? "bg-positive/20 text-positive" : "bg-negative/20 text-negative"} whitespace-nowrap`}>
                    {symbol.change >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                    <span className="ml-0.5">{symbol.change >= 0 ? "+" : ""}{symbol.change}%</span>
                  </div>
                </div>
                
                {/* LTP - Consistent Last Traded Price across mobile and desktop with increased font size for mobile */}
                <div className="flex items-center whitespace-nowrap">
                  <span className="text-xs text-theme-light mr-1">LTP:</span>
                  <span className={`${isMobile ? 'text-base' : ''} font-medium ${getLTPColor()} transition-colors duration-300`}>
                    {consistentLTP > 0 ? formatPrice(consistentLTP) : "--"}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Mobile Expandable Content for BADLA instruments - Using actual related symbols data with proper digit precision */}
            {Object.keys(exchangeData).length > 0 && isBadlaInstrument && isMobile && (
              <Collapsible
              open={isExpanded}
              onOpenChange={setIsExpanded}
                className="w-full"
              >
                {/* Mobile Expandable Trigger */}
                <div className="w-full text-center py-1 border-t border-border/30">
                  <CollapsibleTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-5 w-5 p-0 text-theme-secondary hover:text-theme-light focus:ring-0 focus:outline-none" 
                      aria-label={isExpanded ? "Collapse" : "Expand"}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ArrowDown 
                        size={14} 
                        className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </Button>
                  </CollapsibleTrigger>
                </div>
                
                {/* Mobile Expandable Content - Using proper symbol mapping like desktop with full digit precision */}
                <CollapsibleContent className="overflow-hidden transition-all animate-accordion-down">
                  <div className="p-2 pt-0 border-t border-border/30 mt-1 bg-card/70">
                    {/* Enhanced mobile grid with better spacing for Bid/Ask separation */}
                    <div className="grid grid-cols-3 gap-3 mb-1 text-xs font-medium text-theme-light">
                      <div>Symbol</div>
                      <div className="text-center border-r border-border/30 pr-2">Bid</div>
                      <div className="text-right">Ask</div>
                    </div>
                    
                    {/* Related symbols rows with enhanced spacing and separation and full digit precision */}
                    <div className="space-y-1">
                      {Object.entries(exchangeData).map(([symbolName, prices]: [string, any]) => {
                        const bidColorClass = getRelatedBidColor(prices.bid, prices.previousBid);
                        const askColorClass = getRelatedAskColor(prices.ask, prices.previousAsk);
                        
                        return (
                          <div key={symbolName} className="grid grid-cols-3 gap-3 text-sm">
                            <div className="font-medium truncate text-theme-light">{symbolName}</div>
                            <div className={`text-center ${bidColorClass} transition-colors duration-300 border-r border-border/30 pr-2`}>
                              {formatPrice(prices.bid, prices.digit)}
                            </div>
                            <div className={`text-right ${askColorClass} transition-colors duration-300`}>
                              {formatPrice(prices.ask, prices.digit)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        </Card>
        
      </div>
      
      {/* Chart Modal */}
      <TradingChartModal
        open={isChartModalOpen}
        onOpenChange={setIsChartModalOpen}
        symbol={symbol}
        defaultChartType="candle"
      />

      <MobileTradingDrawer
        open={isMobileDrawerOpen}
        onOpenChange={setIsMobileDrawerOpen}
        symbol={symbol}
        onBuyClick={() => {
          setIsMobileDrawerOpen(false);
          setTradeType("buy");
          setIsTradingModalOpen(true);
        }}
        onSellClick={() => {
          setIsMobileDrawerOpen(false);
          setTradeType("sell");
          setIsTradingModalOpen(true);
        }}
        onChartClick={openChartModalWithCandlestick}
        onAskAIClick={() => {
          setIsMobileDrawerOpen(false);
          if (onAskAIClick) {
            onAskAIClick();
          }
        }}
      />
      
      {/* COMMENTED OUT: Trading Modal */}
      {/* {isTradingModalOpen && (
        <TradingModal
          open={isTradingModalOpen}
          onOpenChange={setIsTradingModalOpen}
          symbol={symbol}
          tradeType={tradeType}
        />
      )} */}
    </>
  );
};

export default TradingCard;
